import posthog from "posthog-js";

import { currentVia } from "./via";

import type { SendSignInLinkBodyTrackingParams } from "@/api";

type TrackingParam = keyof SendSignInLinkBodyTrackingParams;

const REQUESTER_ID_PARAM = "ph_did" satisfies TrackingParam;

/**
 * Params for the sign-in link's continue URL. The link often opens in another browser — an
 * email app's webview, another device — so it carries this browser's anonymous PostHog id and
 * the promo it came through, which the opening browser registers from its URL as usual.
 */
export function signInLinkTrackingParams(): SendSignInLinkBodyTrackingParams {
  // A full record: a param the API adds fails type checking until it's filled here.
  const params: Record<TrackingParam, string | undefined> = {
    [REQUESTER_ID_PARAM]: posthog.get_distinct_id(),
    via: currentVia(),
  };
  return params;
}

/**
 * The anonymous PostHog id of the browser that requested the sign-in link this page was opened
 * from, or null when it is this browser. Read it before signing in: signing in identifies this
 * browser, replacing its anonymous id.
 */
export function signInLinkRequesterId(): string | null {
  const requesterId = new URLSearchParams(window.location.search).get(REQUESTER_ID_PARAM);
  return requesterId && requesterId !== posthog.get_distinct_id() ? requesterId : null;
}

/**
 * Merges the anonymous PostHog histories of both browsers — the one that requested the sign-in
 * link and this one — into the signed-in user right away, so a signup that stops during
 * onboarding still reads as one person. PostHog refuses the merge for an id that already
 * belongs to an identified user.
 */
export function mergeSignInLinkBrowsers(requesterId: string, uid: string): void {
  posthog.alias(requesterId, uid);
  const browserId = posthog.get_distinct_id();
  if (browserId !== uid) {
    posthog.alias(browserId, uid);
  }
}
